/* global common */
import { byUserInteraction, sendAmplitudeEvent } from '@html/_common/utils';

export default function AppBanner() {
  const GET_APP_BUTTON = '.js-scout-component-app-banner-get-app';
  const CLOSE_BUTTON = '.scout-component-app-banner-close-icon';
  const STORAGE_KEY = 'app_banner_dismissed';
  const STORAGE_COUNT_KEY = 'app_banner_dismissed_count';
  const STORAGE_LAST_SEEN_KEY = 'app_banner_last_seen';
  const TOP_BANNER_WRAPPER = '[data-top-banner-wrapper]';
  const APP_BANNER_WRAPPER = '[data-app-banner-wrapper]';
  const FIRST_VISIT_KEY = 'app_banner_first_visit';
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const FREQUENT_WAIT = 14 * ONE_DAY;
  const REGULAR_WAIT = 3 * ONE_DAY;
  const PAGE_DELAY = 2000; // 2 seconds on page for subsequent visits

  let bannerTimer = null;
  let waitTimerActive = false;

  function debug(message, data) {
    // Set to false in production or when ready to merge. Set to true to debug this closure
    const DEBUG = false;
    if (DEBUG) {
      if (data) console.log(`App Banner Debug: ${message}`, data);
      else console.log(`App Banner Debug: ${message}`);
    }
  }

  function getDismissData() {
    try {
      const dismissedTimestamp = localStorage.getItem(STORAGE_KEY) || '0';
      const dismissCount = localStorage.getItem(STORAGE_COUNT_KEY) || '0';
      const lastSeen = localStorage.getItem(STORAGE_LAST_SEEN_KEY) || '0';

      const data = {
        timestamp: parseInt(dismissedTimestamp, 10),
        count: parseInt(dismissCount, 10),
        lastSeen: parseInt(lastSeen, 10),
      };

      debug('Retrieved dismiss data', data);
      return data;
    } catch (e) {
      // In case localStorage is not available
      debug('Error accessing localStorage', e);
      return { timestamp: 0, count: 0, lastSeen: 0 };
    }
  }

  function shouldShowBasedOnDismissHistory() {
    const dismissData = getDismissData();
    const currentTime = new Date().getTime();
    const lastSeen = dismissData.lastSeen || 0;
    const dismissCount = dismissData.count || 0;

    // No dismissals, can show immediately
    if (dismissCount === 0) return { canShow: true };

    // Check if we should show the banner based on dismiss history
    if (dismissCount > 4) {
      // For frequent dismissers (more than 4 times)
      // Production: Wait 14 days, Test: Wait 10 minutes
      const timeDiff = currentTime - lastSeen;
      const waitTime = FREQUENT_WAIT;
      const waitTimeText = '14 days';

      debug(`Frequent dismisser (${dismissCount}). Time since last seen: ${Math.round(timeDiff / 1000)} seconds`);
      debug(`Required wait time: ${waitTimeText} (${waitTime}ms)`);

      if (timeDiff < waitTime) {
        const remainingTime = waitTime - timeDiff;
        debug(`Banner suppressed: need to wait ${Math.round(remainingTime / 1000)} more seconds`);
        return { canShow: false, remainingTime };
      }
    } else if (dismissCount > 0) {
      // For any dismissal
      // Production: Wait 3 days, Test: Wait 45 seconds
      const timeDiff = currentTime - lastSeen;
      const waitTime = REGULAR_WAIT;
      const waitTimeText = '3 days';

      debug(`Normal visitor (${dismissCount}). Time since last seen: ${Math.round(timeDiff / 1000)} seconds`);
      debug(`Required wait time: ${waitTimeText} (${waitTime}ms)`);

      if (timeDiff < waitTime) {
        const remainingTime = waitTime - timeDiff;
        debug(`Banner suppressed: need to wait ${Math.round(remainingTime / 1000)} more seconds`);
        return { canShow: false, remainingTime };
      }
    }

    return { canShow: true };
  }

  function isTopBannerPresent() {
    const $topBanner = $(TOP_BANNER_WRAPPER);
    return $topBanner.length > 0 && !$topBanner.hasClass('hid') && $topBanner.children().length > 0;
  }

  const isSubsequentVisit = () => localStorage.getItem(FIRST_VISIT_KEY) === 'true';

  function setupWaitTimer(remainingTime, callback) {
    if (waitTimerActive) return; // Prevent multiple wait timers

    waitTimerActive = true;
    debug(`Setting up wait timer for ${Math.round(remainingTime / 1000)} seconds`);

    bannerTimer = setTimeout(() => {
      debug('Wait period completed, now setting up normal banner display');
      waitTimerActive = false;
      callback(true); // Skip dismiss history check the second time
    }, remainingTime);
  }

  function setFirstVisitFlag() {
    localStorage.setItem(FIRST_VISIT_KEY, 'true');
    debug('Set first visit flag');
  }

  function showAppBanner() {
    debug('Showing app banner');

    // Hide top banner, show app banner
    $(TOP_BANNER_WRAPPER).addClass('hid');
    $(APP_BANNER_WRAPPER).removeClass('hid');

    // Update last seen timestamp
    const timestamp = new Date().getTime().toString();
    localStorage.setItem(STORAGE_LAST_SEEN_KEY, timestamp);
    debug(`Updated last seen timestamp: ${timestamp}`);

    // Clear any existing timers
    if (bannerTimer) {
      clearTimeout(bannerTimer);
      bannerTimer = null;
    }

    // Mark interaction as handled to avoid duplicate display
    waitTimerActive = false;

    // Set first visit flag if not already set
    if (!isSubsequentVisit()) setFirstVisitFlag();
  }

  function setupSubsequentVisit() {
    debug('Setting up subsequent visit');

    // Show after 30 seconds on the page, regardless of interaction
    debug('Starting 30-second page timer');
    bannerTimer = setTimeout(() => {
      debug('30-second page timer completed');
      showAppBanner();
    }, PAGE_DELAY);
  }

  function setupInteractionListeners(showImmediately) {
    const INTERACTION_DELAY = 15000; // 15 seconds after interaction for first-time visits with top banner

    debug(`Setting up interaction listeners (showImmediately: ${showImmediately})`);

    const onUserInteraction = () => {
      debug('First user interaction detected');

      if (showImmediately) {
        // For first-time visit without top banner: show immediately on interaction
        debug('Showing app banner immediately on interaction');
        showAppBanner();
      } else {
        // For first-time visit with top banner: wait 15 seconds after interaction
        debug('Starting 15-second timer after interaction');
        bannerTimer = setTimeout(() => {
          debug('15-second post-interaction timer completed');
          showAppBanner();
        }, INTERACTION_DELAY);
      }
    };

    const commonJs = typeof sendAmplitudeEvent === 'function' ? {
      sendAmplitudeEvent,
      byUserInteraction,
    } : common;

    // Add event listeners for user interaction
    commonJs.byUserInteraction('initiate-app-banner', onUserInteraction);
    debug('Added interaction event listeners');
  }

  function setupFirstTimeVisitWithTopBanner() {
    debug('Setting up first-time visit with top banner');
    // Wait 15 seconds after user interaction
    setupInteractionListeners(true);
  }

  function setupFirstTimeVisitWithoutTopBanner() {
    debug('Setting up first-time visit without top banner');
    // Show immediately upon user interaction
    setupInteractionListeners(true);
  }

  // Forward declaration of functions that are used before they're defined
  // This is needed to make linting happy
  function setupBannerDisplay(skipDismissCheck = false) {
    debug('Setting up banner display strategy');

    // First check if we should show based on dismiss history
    if (!skipDismissCheck) {
      const dismissCheck = shouldShowBasedOnDismissHistory();

      if (!dismissCheck.canShow) {
        debug('Banner not shown immediately due to dismiss history');

        // Set a timer to show it after the remaining wait time
        if (dismissCheck.remainingTime) setupWaitTimer(dismissCheck.remainingTime, setupBannerDisplay);
        return;
      }
    }

    const topBannerPresent = isTopBannerPresent();
    const subsequentVisit = isSubsequentVisit();

    debug(`Top banner present: ${topBannerPresent}, Subsequent visit: ${subsequentVisit}`);
    // 3. Subsequent Visits: show after 30 seconds on page
    if (subsequentVisit) setupSubsequentVisit();
    // 1. First-time Visit with Top Banner: wait 15 seconds after interaction
    else if (topBannerPresent) setupFirstTimeVisitWithTopBanner();
    // 2. First-time Visit without Top Banner: show immediately on interaction
    else setupFirstTimeVisitWithoutTopBanner();
  }

  function isMobileDevice() {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      && window.innerWidth < 768;
    debug(`Device check - isMobile: ${isMobile}, width: ${window.innerWidth}`);
    return isMobile;
  }

  function isAppWebView() {
    // Check for TourRadar app webview
    const isAppView = /TourRadarApp/i.test(navigator.userAgent);
    debug(`App webview check: ${isAppView}`);
    return isAppView;
  }

  function shouldShowBanner() {
    debug('Checking banner display conditions');

    // English only
    const lang = $('html').attr('lang');
    debug(`Language check: ${lang}`);
    if (lang !== 'en') return debug('Banner hidden: language not English');

    // Mobile web users only (exclude desktop and app webviews)
    if (!isMobileDevice() || isAppWebView()) return debug('Banner hidden: not mobile or is app webview');

    debug('All conditions passed, banner should be shown');
    return true;
  }

  function hideBanner() {
    debug('Hiding app banner');
    $(APP_BANNER_WRAPPER).addClass('hid');
    $(TOP_BANNER_WRAPPER).removeClass('hid');
  }

  function incrementDismissCount() {
    const dismissData = getDismissData();
    const newCount = dismissData.count + 1;

    debug(`Incrementing dismiss count to ${newCount}`);
    localStorage.setItem(STORAGE_COUNT_KEY, newCount.toString());
    localStorage.setItem(STORAGE_KEY, new Date().getTime().toString());
  }

  debug('Initializing App Banner');
  const commonJs = typeof sendAmplitudeEvent === 'function' ? {
    sendAmplitudeEvent,
    byUserInteraction,
  } : common;

  // Skip if not the right environment
  if (!shouldShowBanner()) return debug('Banner initialization aborted: display conditions not met');

  // Set up click event for Get App button
  $(GET_APP_BUTTON).on('click', () => {
    debug('Get App button clicked');
    commonJs.sendAmplitudeEvent({ ampEventName: 'w2a_universal_header_get_app_clicked' });
    setTimeout(() => {
      window.location.href = 'https://tourradar.app.link/universal_header';
    }, 300);
  });

  // Set up click event for Close button
  $(CLOSE_BUTTON).on('click', () => {
    debug('Close button clicked');
    commonJs.sendAmplitudeEvent({ ampEventName: 'w2a_universal_header_dismissed' });
    hideBanner();
    incrementDismissCount();
  });

  // Set up banner display logic based on visit type and banner presence
  setupBannerDisplay();
}
